import { Component, Host, Prop, State, h, Element, Fragment } from '@stencil/core';
import { darkColors, deviceType, featuredColors, imageAspectRatioClass } from './featured-constants';
import { convertToBoolean, getDeviceType, handleKeyDown } from './featured-component-utils';

@Component({
  tag: 'vs-featured-container',
  styleUrl: 'featured-container.css',
  shadow: false,
})

// START GENAI@CHATGPT4
export class ComponentContainer {
  /**
   * JSON string containing the configuration data for the featured container.
   */
  @Prop() data: string = '';
  /**
   * Mode of the featured container- pass when opened in Authoring mode.
   */
  @Prop() mode?: string = '';
  /**
   * Indicates if this is the top component.
   */
  @Prop() isTopComponent?: any = false;
  private componentsRefs: HTMLElement[] = [];
  private iObserver: IntersectionObserver;
  private featureConfig: any = [];
  private featuredItems: any = [];
  @Element() el: HTMLVsFeaturedContainerElement;
  @State() imageDimension: string = imageAspectRatioClass['16:9'];
  @State() activeComponent: number = 0;
  @State() deviceType: string = deviceType.DESKTOP;
  @State() textAlignment: string = 'left';
  private removeBottomMargin: boolean = false;
  private isRTL = false;
  private focusableElements = [];

  componentWillLoad() {
    this.initializeFeatureConfig();
    this.detectDevice();
    window.addEventListener('resize', this.detectDevice.bind(this));
    this.isRTL = document.dir === 'rtl';
    }

  private hashChangeListener = () => {
    const hash = window.location.hash.substring(1);
    const targetElement = this.el.querySelector(`#${hash}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  private initializeFeatureConfig() {
    this.featureConfig = JSON.parse(this.data) || [];
    this.featuredItems = this.featureConfig.featuredItems || [];
    this.imageDimension = imageAspectRatioClass[this.featureConfig.mediaAspectRatio] || imageAspectRatioClass['16:9'];
    this.textAlignment = this.featureConfig.textAlignment || 'left';
    this.removeBottomMargin = convertToBoolean(this.featureConfig.removeBottomMargin);
  }

  private detectDevice() {
    if (this?.mode === 'authoring') {
      this.deviceType = deviceType.TABLET;
      return;
    }
    this.deviceType = getDeviceType();
    if (this.featuredItems.length === 1 && this.deviceType === deviceType.DESKTOP) {
      this.deviceType = deviceType.TABLET;
    }
    // this.deviceType = deviceType.TABLET;
  }

  componentDidLoad() {
    this.setupIntersectionObserver();

    // Initialize focusable elements
    const focusableSelectors = 'a, button,.vs-amb-video-btn , input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
    const allFocusableElements = [];
    const mediaSelector = this.deviceType === deviceType.DESKTOP ? '.vs-featured-screen' : '.vs-fc-s-wrapper';
    const textSelector = '.vs-fc-text-wrapper';

    const mediaContents = Array.from(this.el.querySelectorAll(mediaSelector));
    const textContents = Array.from(this.el.querySelectorAll(textSelector));

    mediaContents.forEach((mediaElem, i) => {
      const focusableMedia = Array.from(mediaElem.querySelectorAll(focusableSelectors));
      const focusableText = Array.from(textContents[i].querySelectorAll(focusableSelectors));
      allFocusableElements.push(...focusableMedia, ...focusableText);
    });
    allFocusableElements?.forEach((val: any, i) => {
      if (val) {
        val.tabIndex = i === 0 ? 0 : -1;
      }
    });
    this.focusableElements = allFocusableElements;

    // Set the initial background color to the first tile's color
    this.updateFeaturedBackground(0);

    this.el.addEventListener('keydown', this.keydownHandler);
    // Add hash change listener
    window.addEventListener('hashchange', this.hashChangeListener);
    if (window.location.hash) {
  this.hashChangeListener();  // Invoke the listener on load to handle direct navigation with hash
}
  }

  disconnectedCallback() {
    if (this.deviceType === deviceType.DESKTOP) {
      this.componentsRefs.forEach(ref => this.iObserver.unobserve(ref));
    }
    this.el.removeEventListener('keydown', this.keydownHandler);
    // Remove hash change listener
    window.removeEventListener('hashchange', this.hashChangeListener);
  }

  private keydownHandler = (event: KeyboardEvent) => {
    handleKeyDown(event, this.focusableElements, this.el);
  };

  private setupIntersectionObserver() {
    const observerConfig = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const onIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetIndex = this.componentsRefs.indexOf(entry.target as HTMLElement);
          this.activeComponent = targetIndex;
          this.updateFeaturedBackground(targetIndex);
          this.updateImageWrapperClasses(targetIndex);
        }
      });
    };

    this.iObserver = new IntersectionObserver(onIntersection, observerConfig);

    if (this.deviceType === deviceType.DESKTOP) {
      this.componentsRefs.forEach(ref => this.iObserver.observe(ref));
    }
  }

  private updateFeaturedBackground(index: number) {
    const featuredContainer = this.el.querySelector('.vs-featured-container') as HTMLElement;
    if (featuredContainer) {
      const bgColor = this.featuredItems[index].backgroundColor;
      featuredContainer.style.background = featuredColors[bgColor] || 'white';
    }
  }

  private updateImageWrapperClasses(activeIndex: number) {
    const imageWrappers = this.el.querySelectorAll('.vs-featuredm-content');
    imageWrappers.forEach((el, index) => {
      el.classList.toggle('vs-featured-faded', index !== activeIndex);
      el.classList.toggle('vs-zindexplus', index === activeIndex);
      el.classList.toggle('vs-zindexminus', index !== activeIndex);

    });
  }

  private formatMediaData(room) {
    return JSON.stringify({
      videoUrl: room.mediaItems.videoUrl,
      videoMediaControlBtn: room?.mediaItems?.videoButtonStyle || 'light',
      poster: room?.mediaItems?.imageUrl,
      accessibility: this.featureConfig.accessibility,
      headerText: room.headerText,
      videoAriaLabel: room.mediaItems.accessibility.imageAltText,
    });
  }
  // START GENAI@CHATGPT4
  private DynamicHeadingComponent = ({ level, htmlString, isDark, id }) => {
    const HeadingTag = level;
    let className = `${isDark ? 'vs-alt' : ''} vs-display-2`;
    if (level === 'h1') {
      className = `vs-display-1 ${isDark ? 'vs-alt' : ''}`;
    }
    if (level === 'h3' || level === 'h4' ) {
      className = `vs-display-3 ${isDark ? 'vs-alt' : ''}`;
    }
    return <HeadingTag id={id} class={`${className}`} innerHTML={htmlString} />;
  };
  // END GENAI@CHATGPT4
  render() {
    return (
      <Host>
        <div
          class={`vs-featured-container vs-featured-text-${this.textAlignment} vs-featured-${this.deviceType} ${convertToBoolean(this.isTopComponent) ? 'top-component' : ''}`}
          id={`featured-container-${this.featureConfig.uniqueId}`}
          dir="ltr"
          role="region"
          aria-label="Featured content section"
        >
          {/* Create a visually hidden list for screen readers to establish reading order */}
          <div class="vs-sr-only" role="list" aria-label="Featured content sections">
            {this.featuredItems?.map((featureItem) => (
              <div role="listitem" id={`featured-group-${featureItem.uniqueId}`}>
                {this.textAlignment === 'right' || this.deviceType===deviceType.MOBILE ? (
                  <>
                    <div id={`media-order-${featureItem.uniqueId}`} aria-owns={`featured-media-${this.deviceType !== deviceType.DESKTOP ? 'tab-' : ''}${featureItem.uniqueId}`}></div>
                    <div id={`content-order-${featureItem.uniqueId}`} aria-owns={`featured-section-${featureItem.uniqueId}`}></div>
                  </>
                ) : (
                  <>
                    <div id={`content-order-${featureItem.uniqueId}`} aria-owns={`featured-section-${featureItem.uniqueId}`}></div>
                    <div id={`media-order-${featureItem.uniqueId}`} aria-owns={`featured-media-${this.deviceType !== deviceType.DESKTOP ? 'tab-' : ''}${featureItem.uniqueId}`}></div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div class="vs-featured-media">
            <div class="vs-video-wrapper">
              {this.featuredItems?.map((featureItem, i) => (
                <div
                  class={`vs-featured-screen vs-featuredm-content ${i > 0 ? 'vs-zindexminus vs-featured-faded' : ''} ${this.imageDimension}`}
                  key={featureItem.uniqueId}
                  data-index={i}
                  id={`featured-media-${featureItem.uniqueId}`}
                  // aria-hidden={i !== this.activeComponent}
                  // aria-labelledby={`content-heading-${featureItem.uniqueId}`}
                >
                  {featureItem.mediaItems.mediaType === 'image' ? (
                    <picture>
                      <img
                        class={`${this.imageDimension} vs-featured-image vs-rounded-image`}
                        src={featureItem.mediaItems.imageUrl}
                        alt={featureItem.mediaItems.accessibility.imageAltText}
                        // aria-describedby={`content-description-${featureItem.uniqueId}`}
                      />
                    </picture>
                  ) : (
                    <div class={`vs-fc-video-container vs-rounded-image`}>
                      <vs-ambient-video
                        tIndex={i === 0 ? 0 : -1}
                        enableRoundedCorners="true"
                        key={`ambient-${featureItem.uniqueId}`}
                        imageDimension={this.imageDimension}
                        data={this.formatMediaData(featureItem)}
                        rootElement={this.el}
                        // aria-describedby={`content-description-${featureItem.uniqueId}`}
                      ></vs-ambient-video>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div class="vs-featured-sections">
            {this.featuredItems?.map((featureItem, i) => (
              <div
                id={`featured-section-${featureItem.uniqueId}`}
                class="vs-featured-section"
                ref={el => (this.componentsRefs[i] = el as HTMLElement)}
                style={{ backgroundColor: this.deviceType !== 'desktop' ? featuredColors[featureItem?.backgroundColor] : 'inherit' }}
                // role="tabpanel"
                aria-label={`feature Content section ${i + 1} of ${this.featuredItems.length}`}
              >
                <div class="vs-fc-section-content">
                <div
                    class={`vs-fc-text-wrapper richtext`}
                    dir={this.isRTL ? 'rtl' : 'ltr'}
                  >
                    <div class="vs-fc-text-container">
                      <div
                        class={`vs-lead ${darkColors?.includes(featureItem.backgroundColor) ? 'vs-alt' : ''}`}
                        innerHTML={featureItem?.eyebrowText}
                      ></div>
                      <this.DynamicHeadingComponent
                        level={featureItem?.headingStyle || 'h2'}
                        htmlString={featureItem.headerText}
                        isDark={darkColors?.includes(featureItem.backgroundColor)}
                        id={`content-heading-${featureItem.uniqueId}`}
                      />
                      <p
                        class={`vs-text vs-featured-bc ${darkColors?.includes(featureItem.backgroundColor) ? 'vs-alt' : ''}`}
                        innerHTML={featureItem.bodyText}
                        id={`content-description-${featureItem.uniqueId}`}
                      ></p>
                    </div>
                    {featureItem.ctas?.map(link => (
                      <div class={`vs-featured-${link?.ctaUniqueId}-element ${this.deviceType === deviceType.MOBILE ? 'vs-full-width' : ''}`}>
                        <vs-link
                          link-aria-label={link.ctaLinkAriaLabel}
                          button-color={link.ctaButtonColor}
                          link-conversion={link.ctaConversionType}
                          link-type={link.ctaLinkType}
                          link-style={link.ctaStyle}
                          has-dark-bg={darkColors?.includes(featureItem.backgroundColor) ? 'yes' : 'no'}
                          link-target={link.ctaLinkTarget}
                          link-text={link.ctaTitle}
                          img-path={link.ctaVideoImagePath}
                          video-title={link.ctaVideoTitle}
                          video-url={link.ctaVideoUrl}
                          link-val={link.ctaLink}
                          modal-target={link.ctaUniqueId}
                          is-no-follow={link?.ctaNoFollow}
                        ></vs-link>
                      </div>
                    ))}
                  </div>
                  <div class={ this.deviceType === deviceType.TABLET ? 'vs-fc-s-wrapper-tablet' : ''}>
                    <div class={`vs-fc-s-wrapper ${this.imageDimension}`} id={`featured-media-tab-${featureItem.uniqueId}`}>
                      <div class="">
                        {featureItem.mediaItems.mediaType === 'image' ? (
                          <picture>
                            <img
                              class={`${this.imageDimension} vs-featured-image vs-rounded-image`}
                              src={featureItem.mediaItems.imageUrl}
                              alt={`${featureItem.mediaItems.accessibility.imageAltText}`}
                            />
                          </picture>
                        ) : null}
                        <div class={`vs-fc-video-container vs-rounded-image vs-tab-video-container`}>
                          {featureItem.mediaItems.mediaType === 'video' ? (
                            <vs-ambient-video
                              enableRoundedCorners="true"
                              key={`ambient-${featureItem.uniqueId}`}
                              imageDimension={this.imageDimension}
                              data={this.formatMediaData(featureItem)}
                            ></vs-ambient-video>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {!this.removeBottomMargin ? <div class="vs-featured-h30" aria-hidden="true"></div> : ''}
      </Host>
    );
  }
}
